// The Lab's geometry: the one file the three readers of a recipe share.
//
// The studio's canvas, the stage page and the render plane all place the
// same element in the same box and move it at the same moment, because they
// all read these numbers. apps/studio/src/lib/lab/geometry.ts is a
// byte-identical copy (a test says so) and rookeryos_render/lab/geometry.py
// mirrors every function (a parity test runs both over the same inputs).
// Zero dependencies, on purpose.

export const W = 1920;
export const H = 1080;
export const FPS = 30;
/** Even seconds only: 30 × s is then a multiple of the 60-frame GOP, so the loop cuts clean. */
export const SECONDS = [6, 8, 10, 12, 14, 16, 18, 20] as const;
export const DEFAULT_SECONDS = 12;
export const MAX_ELEMENTS = 12;
export const MAX_MESSAGES = 6;
export const MARGIN = 64;
/** The live rig's pace (rig.py TICKER_SPEED). */
export const TICKER_SPEED = 110;
export const MOTION_SECONDS = 0.5;

export type Zone = 'lower-third' | 'top-third' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left-rail' | 'right-rail' | 'foot' | 'centre';
export interface Box { x: number; y: number; w: number; h: number }

export const ZONES: Record<Zone, Box> = {
  'lower-third': { x: MARGIN, y: 820, w: W - MARGIN * 2, h: 180 },
  'top-third': { x: MARGIN, y: MARGIN, w: W - MARGIN * 2, h: 160 },
  'top-left': { x: MARGIN, y: MARGIN, w: 420, h: 140 },
  'top-right': { x: W - MARGIN - 420, y: MARGIN, w: 420, h: 140 },
  'bottom-left': { x: MARGIN, y: H - MARGIN - 140, w: 420, h: 140 },
  'bottom-right': { x: W - MARGIN - 420, y: H - MARGIN - 140, w: 420, h: 140 },
  'left-rail': { x: MARGIN, y: 240, w: 360, h: 560 },
  'right-rail': { x: W - MARGIN - 360, y: 240, w: 360, h: 560 },
  foot: { x: 0, y: H - 72, w: W, h: 72 },
  centre: { x: (W - 960) / 2, y: (H - 300) / 2, w: 960, h: 300 },
};

/** A box kept inside the canvas, never smaller than a chip. */
export function clampBox(b: Box): Box {
  const w = Math.max(40, Math.min(W, Math.round(b.w)));
  const h = Math.max(24, Math.min(H, Math.round(b.h)));
  const x = Math.max(0, Math.min(W - w, Math.round(b.x)));
  const y = Math.max(0, Math.min(H - h, Math.round(b.y)));
  return { x, y, w, h };
}

/** The zone a box sits in, when it sits within `tolerance` px of one; else null. */
export function zoneOf(b: Box, tolerance = 24): Zone | null {
  for (const [zone, z] of Object.entries(ZONES) as [Zone, Box][]) {
    if (Math.abs(b.x - z.x) <= tolerance && Math.abs(b.y - z.y) <= tolerance && Math.abs(b.w - z.w) <= tolerance && Math.abs(b.h - z.h) <= tolerance) return zone;
  }
  return null;
}

/** Deterministic 32-bit RNG: the same seed gives the same plate everywhere. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Motion = 'none' | 'slide' | 'wipe' | 'fade';

export interface ElementState {
  /** Offset in px from the element's box (slide), 0 otherwise. */
  dx: number; dy: number;
  /** 0..1 opacity (fade), 1 otherwise. */
  alpha: number;
  /** 0..1 of the box revealed from the left (wipe), 1 otherwise. */
  reveal: number;
  /** Which message a band shows now (index), or 0. */
  message: number;
  /** True while the element is on screen at all. */
  visible: boolean;
}

/**
 * Where an element is at time t of a loop of `seconds`. A band with n messages
 * has n slots of seconds/n; every slot enters and exits over MOTION_SECONDS, so
 * frame 0 and frame seconds are identical and the loop is seamless. Elements
 * without messages hold one slot for the whole loop and only enter once (at
 * the start) and exit at the end.
 */
export function elementState(t: number, seconds: number, messages: number, enter: Motion, exit: Motion, box: Box): ElementState {
  const n = Math.max(1, messages);
  const slot = seconds / n;
  const tt = ((t % seconds) + seconds) % seconds;
  const index = Math.min(n - 1, Math.floor(tt / slot));
  const local = tt - index * slot;
  const m = Math.min(MOTION_SECONDS, slot / 3);
  let phase: 'in' | 'on' | 'out' = 'on';
  let k = 1;
  if (local < m) { phase = 'in'; k = ease(local / m); }
  else if (local > slot - m) { phase = 'out'; k = ease((slot - local) / m); }
  const motion = phase === 'in' ? enter : phase === 'out' ? exit : 'none';
  const st: ElementState = { dx: 0, dy: 0, alpha: 1, reveal: 1, message: index, visible: true };
  if (motion === 'slide') {
    // From the nearer edge: left for the left half of the frame, right otherwise; a foot band rises.
    if (box.w >= W * 0.8) st.dy = Math.round((1 - k) * (box.h + MARGIN));
    else st.dx = Math.round((1 - k) * (box.x + box.w / 2 < W / 2 ? -(box.x + box.w) : W - box.x));
  } else if (motion === 'fade') st.alpha = k;
  else if (motion === 'wipe') st.reveal = k;
  if (phase !== 'on' && k <= 0) st.visible = false;
  return st;
}

function ease(k: number): number {
  const c = Math.max(0, Math.min(1, k));
  return c * c * (3 - 2 * c);
}

/**
 * A ticker crosses the frame a whole number of times per loop, so it too is
 * seamless: the x of the strip's left edge at time t, given the strip's width.
 */
export function tickerX(t: number, seconds: number, stripWidth: number, speed = TICKER_SPEED): number {
  const travel = stripWidth + W;
  const n = Math.max(1, Math.round((speed * seconds) / travel));
  const v = (n * travel) / seconds;
  const tt = ((t % seconds) + seconds) % seconds;
  return W - ((tt * v) % travel);
}

/** The RNG-drawn parameters of a plate, consumed in one fixed order so every reader agrees. */
export interface Streak { y: number; len: number; x0: number; n: number; a: number }
export interface Dot { x: number; y: number; k: number; p: number; r: number }
export function plateSeeds(kind: string, seed: number, density: number): { streaks: Streak[]; dots: Dot[] } {
  const rnd = mulberry32(seed);
  const d = Math.max(0.2, Math.min(3, density));
  const streaks: Streak[] = [];
  const dots: Dot[] = [];
  const ns = Math.round(10 * d);
  for (let i = 0; i < ns; i++) streaks.push({ y: rnd() * H, len: 300 + rnd() * 900, x0: rnd() * (W + 1200), n: 1 + Math.floor(rnd() * 3), a: 0.05 + rnd() * 0.12 });
  const nd = Math.round(180 * d);
  for (let i = 0; i < nd; i++) dots.push({ x: rnd() * W, y: rnd() * H, k: 1 + Math.floor(rnd() * 3), p: rnd(), r: 1.5 + rnd() * 3 });
  void kind;
  return { streaks, dots };
}
